class StickyProductBar {
  constructor() {
    this.stickyBar = document.querySelector('[data-sticky-product-bar]');
    this.recommendationsSection = null;
    this.isVisible = false;
    this.product = null;

    if (!this.stickyBar) return;

    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Load product data
    this.loadProductData();

    // Find the product recommendations section
    this.findRecommendationsSection();

    if (!this.recommendationsSection) {
      console.warn('Product recommendations section not found');
      return;
    }

    // Set up scroll listener
    this.setupScrollListener();

    // Set up variant change listener
    this.setupVariantListener();

    // Set up form submission
    this.setupFormSubmission();
  }

  loadProductData() {
    const productJson = document.querySelector('[data-sticky-product-json]');
    if (productJson) {
      try {
        this.product = JSON.parse(productJson.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }
  }

  findRecommendationsSection() {
    // Primary: look for product-recommendations element
    this.recommendationsSection = document.querySelector('product-recommendations');

    // Fallback: try to find by test ID or class
    if (!this.recommendationsSection) {
      this.recommendationsSection = document.querySelector('[data-testid="product-recommendations-section"]');
    }

    // Last resort: find by heading text
    if (!this.recommendationsSection) {
      const sections = document.querySelectorAll('section, .section');
      for (const section of sections) {
        const heading = section.querySelector('h3, h2, .h3, .h4');
        if (heading && heading.textContent.trim().includes('You may also like')) {
          this.recommendationsSection = section;
          return;
        }
      }
    }
  }

  setupScrollListener() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.checkVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    this.checkVisibility();
  }

  checkVisibility() {
    if (!this.recommendationsSection) return;

    const rect = this.recommendationsSection.getBoundingClientRect();
    const sectionHeight = rect.height;
    const viewportHeight = window.innerHeight;

    // Calculate visible portion of section
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(viewportHeight, rect.bottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    const visiblePercentage = (visibleHeight / sectionHeight) * 100;
    const isInView = visiblePercentage >= 50;

    // Show when at least 50% of recommendations section is in viewport
    if (isInView && !this.isVisible) {
      this.show();
    }
    // Hide when less than 50% is in view
    else if (!isInView && this.isVisible) {
      this.hide();
    }
  }

  show() {
    this.isVisible = true;
    this.stickyBar.classList.add('is-visible');
    document.body.classList.add('sticky-product-bar-active');
  }

  hide() {
    this.isVisible = false;
    this.stickyBar.classList.remove('is-visible');
    document.body.classList.remove('sticky-product-bar-active');
  }

  setupVariantListener() {
    // Listen for variant update events from the theme
    document.addEventListener('variant:update', (event) => {
      if (!event.detail || !event.detail.resource) return;

      // Make sure this is for the correct product
      const productId = this.stickyBar.dataset.productId;
      if (event.detail.data && event.detail.data.productId &&
          event.detail.data.productId !== productId) {
        return;
      }

      this.updateStickyBarContent(event.detail.resource);
    });

    // Also listen for variant selection (for initial selection tracking)
    document.addEventListener('variant:selected', (event) => {
      if (!event.detail || !event.detail.resource) return;

      const variantId = event.detail.resource.id;
      const variant = this.getVariantById(variantId);

      if (variant) {
        this.updateStickyBarContent(variant);
      }
    });
  }

  getVariantById(variantId) {
    if (!this.product || !this.product.variants) return null;
    return this.product.variants.find(v => String(v.id) === String(variantId));
  }

  updateStickyBarContent(variant) {
    if (!variant) return;

    // Update variant ID in hidden input
    const variantInput = this.stickyBar.querySelector('[data-sticky-variant-id]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    // Update variant title
    const variantText = this.stickyBar.querySelector('[data-sticky-variant]');
    if (variantText && variant.title && variant.title !== 'Default Title') {
      const escapedTitle = this.escapeHtml(variant.title);
      variantText.innerHTML = `<span class="sticky-product-bar__variant-text">${escapedTitle}</span>`;
    } else if (variantText) {
      variantText.innerHTML = '';
    }

    // Update price
    const priceElement = this.stickyBar.querySelector('[data-sticky-price]');
    if (priceElement && variant.price !== undefined) {
      const formattedPrice = this.formatMoney(variant.price);
      priceElement.innerHTML = `<span class="sticky-product-bar__price-text">${formattedPrice}</span>`;
    }

    // Update button state
    const addToCartButton = this.stickyBar.querySelector('[data-sticky-add-to-cart]');
    if (addToCartButton) {
      const available = variant.available !== undefined ? variant.available : true;
      if (available) {
        addToCartButton.disabled = false;
        addToCartButton.querySelector('span').textContent = addToCartButton.dataset.addToCartText || 'Add to Cart';
      } else {
        addToCartButton.disabled = true;
        addToCartButton.querySelector('span').textContent = addToCartButton.dataset.soldOutText || 'Sold Out';
      }
    }

    // Update image if variant has featured image
    const image = this.stickyBar.querySelector('[data-sticky-image]');
    if (image && variant.featured_image) {
      const imageUrl = variant.featured_image.src || variant.featured_image;
      if (imageUrl) {
        image.src = this.getSizedImageUrl(imageUrl, '100x100');
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback to simple formatting
    return `$${(cents / 100).toFixed(2)}`;
  }

  getSizedImageUrl(src, size) {
    if (!src) return src;
    // Shopify image URL formatting - add size before extension
    return src.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, `_${size}.$1$2`);
  }

  setupFormSubmission() {
    const form = this.stickyBar.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const formData = new FormData(form);
      const button = form.querySelector('button[type="submit"]');

      if (!button) return;

      button.disabled = true;
      const originalText = button.querySelector('span')?.textContent || '';
      const buttonSpan = button.querySelector('span');
      if (buttonSpan) buttonSpan.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(Object.fromEntries(formData))
        });

        const data = await response.json();

        if (response.ok) {
          // Dispatch cart update event (theme's event system)
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: form.id,
              data: {
                source: 'sticky-product-bar',
                variantId: data.variant_id,
                itemCount: data.quantity
              }
            }
          }));

          // Open cart drawer if it exists and auto-open is enabled
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        } else {
          console.error('Failed to add to cart:', data);
          // Dispatch error event
          document.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              sourceId: form.id,
              data: {
                message: data.message || 'Failed to add to cart',
                description: data.description,
                errors: data.errors
              }
            }
          }));
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        button.disabled = false;
        if (buttonSpan) buttonSpan.textContent = originalText;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyProductBar();
  });
} else {
  new StickyProductBar();
}
